// Drill-down por indústria: Indústria → BU → sellers daquela indústria → pedidos → itens.
// "Seus produtos estão sofrendo corte no Seller X."
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getIndustriaResumo, IndustriaResumo } from '../api';
import { getPrefs } from '../auth';
import KpiCard from '../components/KpiCard';
import Dica from '../components/Dica';
import { AJUDA } from '../utils/ajudas';
import TrendChart from '../components/TrendChart';
import { KpisJanelas } from '../types';
import { fmtDataHoraFoto, fmtMoney, fmtNum, fmtPct, fmtPp } from '../utils/format';

const delta = (k: KpisJanelas): number | null =>
  (k.mtd.ns_qtd !== null && k.m1.ns_qtd !== null ? k.mtd.ns_qtd - k.m1.ns_qtd : null);

const deltaCls = (d: number | null) => (d === null ? '' : d > 0 ? 'delta-good' : d < 0 ? 'delta-bad' : '');

export default function Industria() {
  const { id } = useParams();
  const industriaId = Number(id);
  const [params, setParams] = useSearchParams();
  const buId = params.get('bu') ? Number(params.get('bu')) : null;
  const navigate = useNavigate();
  const prefs = getPrefs();

  const [data, setData] = useState<IndustriaResumo | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    setData(null);
    getIndustriaResumo(industriaId, prefs.janela_ref || 'YTD', { de: prefs.custom_de, ate: prefs.custom_ate }, buId)
      .then(setData)
      .catch((e: Error) => setErro(e.message));
  }, [industriaId, buId]);

  if (erro) return <div className="card"><p className="empty">{erro}</p><Link to="/service-level/dashboard">← Voltar</Link></div>;
  if (!data) return <p className="empty">Carregando indústria…</p>;

  const k = data.kpis;

  return (
    <>
      <div className="page-header">
        <div>
          <button className="btn secondary mb-2" onClick={() => navigate(-1)}><ArrowLeft size={14} /> Voltar</button>
          <h1>{data.industria.nome}{data.bu ? ` · ${data.bu.sigla}` : ''}</h1>
          <div className="sub">
            {data.sellers.length} sellers com volume neste recorte
            {data.bu && (
              <>
                {' · '}
                <a href="#" onClick={(e) => { e.preventDefault(); setParams({}); }}>limpar BU ×</a>
              </>
            )}
          </div>
        </div>
        <span className="foto-badge">Última atualização {fmtDataHoraFoto(data.ts_foto)}</span>
      </div>

      <div className="kpi-row">
        <KpiCard
          label="NS Quantidade (Fill Rate)" ajuda={AJUDA.ns}
          valores={{ hoje: k.hoje.ns_qtd, mtd: k.mtd.ns_qtd, m1: k.m1.ns_qtd, ref: k.ref.ns_qtd }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
          extra={`sobre pedidos já apurados (${fmtPct(k.mtd.pct_apurado)} do volume do mês)`}
        />
        <KpiCard
          label="Pedidos no Prazo" ajuda={AJUDA.prazo}
          valores={{ hoje: k.hoje.pct_no_prazo, mtd: k.mtd.pct_no_prazo, m1: k.m1.pct_no_prazo, ref: k.ref.pct_no_prazo }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
        />
        <KpiCard
          label="Corte" ajuda={AJUDA.corte}
          valores={{ hoje: k.hoje.pct_corte, mtd: k.mtd.pct_corte, m1: k.m1.pct_corte, ref: k.ref.pct_corte }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="down"
          extra={`${fmtMoney(k.mtd.corte_valor)} cortados no mês`}
        />
        <KpiCard
          label="Apuração do mês" ajuda={AJUDA.apuracao}
          valores={{ hoje: k.hoje.pct_apurado ?? null, mtd: k.mtd.pct_apurado ?? null, m1: k.m1.pct_apurado ?? null, ref: k.ref.pct_apurado ?? null }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
          extra={`${fmtNum(k.mtd.pedidos_em_aberto)} pedidos em aberto, aguardando apuração`}
        />
      </div>

      <div className="card">
        <h2 className="flex items-start justify-between gap-2"><span>NS de quantidade — tendência diária do mês</span><Dica titulo="Tendência diária" texto={AJUDA.tendencia} /></h2>
        <TrendChart serie={data.serie_ns} />
      </div>

      {!data.bu && data.por_bu.length > 0 && (
        <div className="card">
          <h2>Por BU — clique para aprofundar</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>BU</th>
                  <th className="num">NS Qtd (MTD)</th>
                  <th className="num">Δ vs M-1</th>
                  <th className="num">No prazo (MTD)</th>
                  <th className="num">Corte (MTD)</th>
                </tr>
              </thead>
              <tbody>
                {[...data.por_bu]
                  .sort((a, b) => (a.kpis.mtd.ns_qtd ?? 1) - (b.kpis.mtd.ns_qtd ?? 1))
                  .map(({ bu, kpis }) => {
                    const d = delta(kpis);
                    return (
                      <tr key={bu.id} className="clickable" onClick={() => setParams({ bu: String(bu.id) })}>
                        <td><strong>{bu.sigla}</strong></td>
                        <td className="num"><strong>{fmtPct(kpis.mtd.ns_qtd)}</strong></td>
                        <td className={`num ${deltaCls(d)}`}>{fmtPp(d)}</td>
                        <td className="num">{fmtPct(kpis.mtd.pct_no_prazo)}</td>
                        <td className="num">{fmtMoney(kpis.mtd.corte_valor)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Sellers neste recorte — pior NS no topo</h2>
        <div className="desktop-only">
          <table>
            <thead>
              <tr>
                <th>Seller</th>
                <th className="num">NS Qtd (MTD)</th>
                <th className="num">Δ vs M-1</th>
                <th className="num">No prazo (MTD)</th>
                <th className="num">Corte (MTD)</th>
              </tr>
            </thead>
            <tbody>
              {data.sellers.map(({ seller, kpis }) => {
                const d = delta(kpis);
                return (
                  <tr key={seller.seller_id}>
                    <td>
                      <Link to={`/service-level/sellers/${seller.seller_id}?industria=${industriaId}`}><strong>{seller.nome_fantasia}</strong></Link>
                      <div className="muted text-[11px]">{seller.uf_base}</div>
                    </td>
                    <td className="num"><strong>{fmtPct(kpis.mtd.ns_qtd)}</strong></td>
                    <td className={`num ${deltaCls(d)}`}>{fmtPp(d)}</td>
                    <td className="num">{fmtPct(kpis.mtd.pct_no_prazo)}</td>
                    <td className="num">{fmtMoney(kpis.mtd.corte_valor)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mobile-only">
          {data.sellers.map(({ seller, kpis }) => {
            const d = delta(kpis);
            return (
              <Link key={seller.seller_id} to={`/service-level/sellers/${seller.seller_id}?industria=${industriaId}`} className="ranking-card">
                <div>
                  <div className="nome">{seller.nome_fantasia}</div>
                  <div className="sub">{fmtPct(kpis.mtd.pct_no_prazo)} no prazo · corte {fmtMoney(kpis.mtd.corte_valor)}</div>
                </div>
                <div>
                  <div className="ns">{fmtPct(kpis.mtd.ns_qtd)}</div>
                  <div className={`sub text-right ${deltaCls(d)}`}>{fmtPp(d)} vs M-1</div>
                </div>
              </Link>
            );
          })}
        </div>
        <p className="chart-note">Os KPIs de cada seller consideram apenas os produtos {data.bu ? `da BU ${data.bu.sigla}` : `de ${data.industria.nome}`}.</p>
      </div>
    </>
  );
}
